"use client";

import { useEffect, useMemo, useState, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Delete, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type PublicUser = { id: number; name: string; emoji: string; owner: string };

type Step = "pickUser" | "pin";

export function LoginView() {
  const router = useRouter();
  const sp = useSearchParams();
  const next = sp.get("next") || "/";

  const [users, setUsers] = useState<PublicUser[] | null>(null);
  const [picked, setPicked] = useState<PublicUser | null>(null);
  const [pin, setPin] = useState("");
  const [step, setStep] = useState<Step>("pickUser");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [remember, setRemember] = useState(true);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [tick, setTick] = useState(0);

  // 사용자 목록 로드
  useEffect(() => {
    let alive = true;
    fetch("/api/auth/users")
      .then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        if (d?.ok && Array.isArray(d.users)) setUsers(d.users);
        else setUsers([]);
      })
      .catch(() => {
        if (alive) setUsers([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  // 잠금 카운트다운
  useEffect(() => {
    if (!lockedUntil) return;
    const id = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const lockRemaining = useMemo(() => {
    if (!lockedUntil) return 0;
    void tick; // re-render trigger
    return Math.max(0, lockedUntil - Date.now());
  }, [lockedUntil, tick]);

  useEffect(() => {
    if (lockedUntil && lockRemaining === 0) {
      setLockedUntil(null);
      setErrorMsg(null);
    }
  }, [lockedUntil, lockRemaining]);

  // PIN 4자리 입력 완료 → 자동 검증
  const submittedRef = useRef(false);
  useEffect(() => {
    if (pin.length !== 4 || !picked || submitting || lockedUntil) return;
    if (submittedRef.current) return;
    submittedRef.current = true;

    (async () => {
      setSubmitting(true);
      setErrorMsg(null);
      try {
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ userId: picked.id, pin, remember }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data?.ok) {
          // localStorage에도 owner 저장 — 기존 budget UI(useCurrentUser)와 호환
          try {
            const owner = data.user?.owner;
            if (owner) localStorage.setItem("budget:currentUser", owner);
            // 자비스 프로필도 동기화
            const profile = {
              id: data.user.owner,
              name: data.user.name,
              emoji: data.user.emoji,
              createdAt: new Date().toISOString(),
            };
            localStorage.setItem("jarvis.profile", JSON.stringify(profile));
            queueMicrotask(() => window.dispatchEvent(new Event("jarvis:profile-change")));
          } catch {}
          // 라운드 93 (피드백 #14): 사용자 전환 시 router.replace+refresh로는 client state가
          // 유지되어 이전 사용자 정보(인사말/owner-bound 데이터)가 그대로 남는 문제.
          // hard reload로 모든 컴포넌트 새 마운트 → 새 사용자 데이터로 깨끗이 재구성.
          window.location.href = next || "/";
          return;
        }
        // 실패
        if (data?.error === "locked") {
          setLockedUntil(Number(data.lockedUntil) || Date.now() + 5 * 60_000);
          setErrorMsg(`5회 실패 — ${data.lockMinutes ?? 5}분 잠금`);
        } else if (data?.error === "wrong_pin") {
          const left = Number(data.remainingAttempts);
          setErrorMsg(left > 0 ? `PIN 틀림 (남은 시도 ${left}회)` : "PIN 틀림");
        } else {
          setErrorMsg("로그인 실패");
        }
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin("");
      } catch {
        setErrorMsg("네트워크 오류");
        setShake(true);
        setTimeout(() => setShake(false), 400);
        setPin("");
      } finally {
        setSubmitting(false);
        submittedRef.current = false;
      }
    })();
  }, [pin, picked, submitting, lockedUntil, remember, next, router]);

  const onPickUser = (u: PublicUser) => {
    setPicked(u);
    setPin("");
    setErrorMsg(null);
    setStep("pin");
  };

  const onTapDigit = (d: string) => {
    if (pin.length >= 4 || submitting || lockedUntil) return;
    setPin((p) => (p + d).slice(0, 4));
  };

  const onBackspace = () => {
    if (submitting) return;
    setPin((p) => p.slice(0, -1));
  };

  if (users === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="animate-spin text-[var(--muted)]" size={28} />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="mx-auto flex min-h-screen max-w-sm flex-col items-center justify-center p-6 text-center">
        <div className="text-2xl">😶</div>
        <div className="mt-2 text-base font-semibold">사용자가 없어</div>
        <div className="mt-1 text-sm text-[var(--muted)]">DB 시드가 안 된 것 같다 — 자비스 로그 확인</div>
      </div>
    );
  }

  return (
    <div
      className="mx-auto flex min-h-screen max-w-sm flex-col px-6 py-12"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 3rem)" }}
    >
      <div className="mb-10 text-center">
        <div className="text-3xl">🏠</div>
        <div className="mt-2 text-2xl font-bold">수수홈</div>
        <div className="mt-1 text-sm text-[var(--muted)]">
          {step === "pickUser" ? "누구세요?" : `${picked?.name}, PIN 입력`}
        </div>
      </div>

      {step === "pickUser" && (
        <div className="grid grid-cols-2 gap-3">
          {users.map((u) => (
            <button
              key={u.id}
              type="button"
              onClick={() => onPickUser(u)}
              className="flex aspect-square flex-col items-center justify-center gap-2 rounded-3xl bg-[var(--surface)] transition active:scale-95"
            >
              <span className="text-5xl">{u.emoji}</span>
              <span className="text-base font-semibold">{u.name}</span>
            </button>
          ))}
        </div>
      )}

      {step === "pin" && picked && (
        <>
          <div className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => {
                setStep("pickUser");
                setPin("");
                setErrorMsg(null);
              }}
              className="mb-3 flex flex-col items-center gap-1 active:opacity-70"
            >
              <span className="text-5xl">{picked.emoji}</span>
              <span className="text-base font-semibold">{picked.name}</span>
              <span className="text-[11px] text-[var(--muted)]">탭해서 변경</span>
            </button>

            <div
              className={cn(
                "mt-6 flex gap-3 transition-transform",
                shake && "animate-[shake_0.3s_ease-in-out]",
              )}
            >
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={cn(
                    "h-4 w-4 rounded-full border-2 transition-all",
                    i < pin.length
                      ? "border-[var(--accent)] bg-[var(--accent)]"
                      : "border-[var(--border)]",
                  )}
                />
              ))}
            </div>

            <div className="mt-3 h-5 text-center text-xs">
              {lockedUntil && lockRemaining > 0 ? (
                <span className="text-orange-400">
                  잠금 — {Math.ceil(lockRemaining / 1000)}초 뒤 다시 시도
                </span>
              ) : errorMsg ? (
                <span className="text-red-400">{errorMsg}</span>
              ) : submitting ? (
                <span className="text-[var(--muted)]">확인 중…</span>
              ) : (
                <span className="text-[var(--muted)]">PIN 4자리</span>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onTapDigit(d)}
                disabled={!!lockedUntil || submitting}
                className="rounded-2xl bg-[var(--surface)] py-4 text-2xl font-semibold transition active:scale-95 active:bg-[var(--surface-2)] disabled:opacity-40"
              >
                {d}
              </button>
            ))}
            <span aria-hidden />
            <button
              type="button"
              onClick={() => onTapDigit("0")}
              disabled={!!lockedUntil || submitting}
              className="rounded-2xl bg-[var(--surface)] py-4 text-2xl font-semibold transition active:scale-95 active:bg-[var(--surface-2)] disabled:opacity-40"
            >
              0
            </button>
            <button
              type="button"
              onClick={onBackspace}
              disabled={submitting}
              className="flex items-center justify-center rounded-2xl bg-[var(--surface)] py-4 transition active:scale-95 active:bg-[var(--surface-2)] disabled:opacity-40"
            >
              <Delete size={22} />
            </button>
          </div>

          <label className="mt-6 flex items-center justify-center gap-2 text-sm text-[var(--muted)]">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 accent-[var(--accent)]"
            />
            <span>30일 동안 기억하기</span>
          </label>
        </>
      )}

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-8px); }
          40% { transform: translateX(8px); }
          60% { transform: translateX(-6px); }
          80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
